/**
 * CompletionModal — dialog semantics + focus management.
 *
 * Per §36, §37 of the Bucket-B briefing:
 *   - Renders ONLY when `open=true` (controlled by the reducer).
 *   - Idempotent: refresh / re-open of an already-completed profile
 *     does NOT re-open the modal — the reducer drives `open` from
 *     `justCompletedFirstTime`, which is true only on the first
 *     CREATE -> active transition.
 *   - Russian copy: "Поздравляем с успешной регистрацией".
 *   - Dismiss/close is sufficient (no mandatory redirect).
 *   - role="dialog" + aria-modal + aria-labelledby for screen readers.
 *   - Focuses the OK button on open.
 */

import { useEffect, useRef } from 'react';

export interface CompletionModalProps {
  readonly open: boolean;
  readonly onDismiss: () => void;
  readonly title?: string;
  readonly body?: string;
  readonly ctaLabel?: string;
}

export function CompletionModal(props: CompletionModalProps) {
  const { open, onDismiss } = props;
  const title = props.title ?? 'Поздравляем с успешной регистрацией';
  const body =
    props.body ??
    'Ваша анкета создана. Вы уже можете открывать её в поиске.';
  const ctaLabel = props.ctaLabel ?? 'Хорошо';
  const ref = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (open) {
      ref.current?.focus();
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="pq-modal-backdrop"
      data-testid="profile-questionnaire-completion-modal"
    >
      <div
        className="pq-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pq-modal-title"
        aria-describedby="pq-modal-body"
      >
        <h2 id="pq-modal-title" className="pq-modal-title">
          {title}
        </h2>
        <p id="pq-modal-body" className="pq-modal-body">
          {body}
        </p>
        <button
          type="button"
          className="pq-modal-cta"
          ref={ref}
          onClick={onDismiss}
          data-testid="profile-questionnaire-completion-modal-cta"
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}